import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'

import { cn } from '@/shared/lib/cn'

const controlClasses =
  'w-full rounded-md border border-[var(--hairline-strong)] bg-ink-700 px-3 py-2 text-base ' +
  'text-linen-200 placeholder:text-thread-500 transition-colors duration-[var(--dur-fast)] ' +
  'hover:border-thread-500 focus:border-bordeaux-400 disabled:opacity-40'

export interface FieldProps {
  label: string
  /** Nasconde l'etichetta visivamente mantenendola per gli assistenti vocali. */
  hideLabel?: boolean
  /** Una riga di aiuto: cosa serve, non come funziona il sistema. */
  hint?: string
  error?: string
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode
  className?: string
}

/**
 * Campo di modulo: etichetta, controllo, aiuto ed errore già collegati fra
 * loro con gli id giusti. I componenti sotto non devono ricordarsene.
 */
export function Field({ label, hideLabel, hint, error, children, className }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className={cn('type-eyebrow', hideLabel && 'sr-only')}>
        {label}
      </label>
      {children({ id, describedBy })}
      {hint ? (
        <p id={hintId} className="text-thread-500 text-meta">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-ruggine-400 text-meta">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export type TextInputProps = InputHTMLAttributes<HTMLInputElement>

export function TextInput({ className, ...rest }: TextInputProps) {
  return <input className={cn(controlClasses, className)} {...rest} />
}

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export function TextArea({ className, ...rest }: TextAreaProps) {
  return <textarea className={cn(controlClasses, 'min-h-20 resize-y', className)} {...rest} />
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

export function Select({ className, children, ...rest }: SelectProps) {
  return (
    <select className={cn(controlClasses, 'cursor-pointer', className)} {...rest}>
      {children}
    </select>
  )
}
